import * as Api from '@ViewModels';

export type TestimonialTemplateSource = 'GoogleReview' | 'SurveyResponse';

export interface TestimonialTemplateValues {
	backgroundColor: string;
	foregroundColor: string;
	backgroundImage?: string | null;
	reviewText: string;
	authorName: string;
	rating: number;
	includeGoogleLogo: boolean;
	includeStars: boolean;
	includeAuthor: boolean;
	source: TestimonialTemplateSource;
	layout?: Partial<Api.IGoogleReviewPostLayout> | null;
}

const PLACEHOLDER_PATTERN = /\{\{(\w+)\}\}/g;

const HEX_COLOR_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const URL_BREAKOUT_PATTERN = /['"\\\r\n]/;

const STAR_FILLED = '#FFD700';
const STAR_EMPTY = 'transparent';
const SHOWN = 'block';
const HIDDEN = 'none';
const STAR_COUNT = 5;

export class TestimonialTemplateError extends Error {}

const escapeHtml = (value: string): string =>
	value
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');

const readColor = (value: string, fieldName: string): string => {
	if (!value || !HEX_COLOR_PATTERN.test(value)) {
		throw new TestimonialTemplateError(`${fieldName} must be a hex colour such as #1F64DC.`);
	}
	return value;
};

const readBackgroundImage = (value: string | null | undefined): string => {
	if (!value) {
		return HIDDEN;
	}
	if (URL_BREAKOUT_PATTERN.test(value)) {
		throw new TestimonialTemplateError('The background image URL may not contain quotes, backslashes or newlines.');
	}
	return `url('${value}')`;
};

const display = (visible: boolean): string => (visible ? SHOWN : HIDDEN);

const box = (
	supplied: Api.IGoogleReviewPostLayoutBox | undefined,
	fallback: Api.IGoogleReviewPostLayoutBox
): Api.IGoogleReviewPostLayoutBox => supplied ?? fallback;

export const interpolateTestimonialTemplate = (
	template: Api.IGoogleReviewPostTemplateDocument,
	values: TestimonialTemplateValues
): string => {
	if (values.rating < 0 || values.rating > STAR_COUNT) {
		throw new TestimonialTemplateError(`Rating must be between 0 and ${STAR_COUNT}.`);
	}

	const defaults = template.defaultLayout;
	const logo = box(values.layout?.logo, defaults.logo);
	const stars = box(values.layout?.stars, defaults.stars);
	const text = box(values.layout?.text, defaults.text);
	const author = box(values.layout?.author, defaults.author);

	const tokens: Record<string, string> = {
		reviewText: escapeHtml(values.reviewText ?? ''),
		authorName: escapeHtml(values.authorName ?? ''),
		backgroundColor: readColor(values.backgroundColor, 'backgroundColor'),
		foregroundColor: readColor(values.foregroundColor, 'foregroundColor'),
		backgroundImage: readBackgroundImage(values.backgroundImage),
		logoGoogleDisplay: display(values.source === 'GoogleReview' && values.includeGoogleLogo),
		logoLevitateDisplay: display(values.source === 'SurveyResponse'),
		starsDisplay: display(values.includeStars && values.rating > 0),
		authorDisplay: display(values.includeAuthor),
		logoX: String(logo.x),
		logoY: String(logo.y),
		logoWidth: String(logo.width),
		logoHeight: String(logo.height),
		starsX: String(stars.x),
		starsY: String(stars.y),
		starsWidth: String(stars.width),
		starsHeight: String(stars.height),
		textX: String(text.x),
		textY: String(text.y),
		textWidth: String(text.width),
		textHeight: String(text.height),
		textFontSize: String(text.fontSize ?? defaults.text.fontSize),
		authorX: String(author.x),
		authorY: String(author.y),
		authorWidth: String(author.width),
		authorHeight: String(author.height),
		authorFontSize: String(author.fontSize ?? defaults.author.fontSize),
	};

	for (let star = 1; star <= STAR_COUNT; star++) {
		tokens[`star${star}Fill`] = star <= values.rating ? STAR_FILLED : STAR_EMPTY;
	}

	let missing: string | null = null;
	const rendered = template.html.replace(PLACEHOLDER_PATTERN, (match, name: string) => {
		if (Object.hasOwn(tokens, name)) {
			return tokens[name];
		}
		missing ??= name;
		return match;
	});

	if (missing != null) {
		throw new TestimonialTemplateError(`The testimonial template declares an unknown placeholder '${missing}'.`);
	}

	return rendered;
};

export interface TestimonialPreview {
	previewHtml: string | null;
	previewError: string | null;
}

export const buildTestimonialPreview = (
	template: Api.IGoogleReviewPostTemplateDocument | undefined,
	values: TestimonialTemplateValues
): TestimonialPreview => {
	if (!template) {
		return { previewHtml: null, previewError: null };
	}

	try {
		return { previewHtml: interpolateTestimonialTemplate(template, values), previewError: null };
	} catch (error) {
		return {
			previewHtml: null,
			previewError:
				error instanceof TestimonialTemplateError ? error.message : 'Unable to render the testimonial preview.',
		};
	}
};
